import { Controller } from '@hotwired/stimulus'
import { jquery } from 'utils/jquery'

// NProgress and Rails are loaded globally via Sprockets.
declare const NProgress: { start(): void; done(): void }
declare const Rails: { fire(element: Element, eventName: string): boolean }

// Modern staged waiting overlay (voter-list-processing-overlay), upload phase. Reused verbatim
// from processing_voters_component_controller.js's own HTML_LOCK_CLASS/OVERLAY_HIDDEN_CLASS — kept
// as separate literals (not a shared import) since these two Stimulus controllers are otherwise
// fully independent; the class names themselves are what's shared (see the SCSS file they both
// point at, processing_overlay_card_component.scss). Keep these three in sync if ever renamed.
const HTML_LOCK_CLASS = 'voter-list-processing-busy'
const OVERLAY_HIDDEN_CLASS = 'voter-list-processing-overlay--hidden'

/**
 * ImportMethodSelectorComponent controller
 *
 * Handles the simple interactive behaviors previously delegated via jQuery
 * in elections.voter_list.coffee:
 *
 *  - .upload-container file input change → the CSV upload, fired on drop OR pick (no Upload button
 *                                  since 2026-08-11): [modern overlay] show overlay + lock /
 *                                  [flag-off] NProgress.start, + Rails UJS form submit
 *  - .upload_button       click  → same sequence. Retained, but nothing in this panel renders that
 *                                  class any more — the drop zone replaced the button (the one
 *                                  remaining `.upload_button` in the app is the admin APIs
 *                                  dashboard, outside this controller's element and so unreachable
 *                                  by this delegated listener).
 *  - .import_button       click  → NProgress.start + label swap + UJS submit
 *  - .parse_data_option   change → UJS form submit
 *  - .import_voter_list   click  → NProgress.start (cosmetic) — SKIPPED when the modern overlay
 *                                  flag is on: the appended ProcessingVotersComponent supplies its
 *                                  own overlay+lock for that whole run
 *  - .validate_voter_list click  → same as .import_voter_list
 *  - .start_over_mapper   click  → NProgress.start  (cosmetic, unchanged — out of scope)
 *  - .start_over_voter_list click → NProgress.start (cosmetic, unchanged — out of scope)
 *  - .start_over_mapper / .start_over_voter_list confirm:complete → NProgress.done
 *  - upload form ajax:complete → [modern overlay only] hide overlay + unlock (covers both the
 *                                  success re-render branches and the @error branch of
 *                                  voter_list_uploads/create.js.erb — its `NProgress.done()` stays
 *                                  a safe no-op either way)
 *  - #duplicate_wizard_path     click → modal without click-close (jQuery)
 *  - #submit_wizard_duplication click → hide #error_div, disable button
 *
 * All listeners use delegated attachment on this.element so they survive
 * inner-DOM replacements made by Rails UJS .js.erb responses.
 *
 * NOT handled here (still in elections.voter_list.coffee):
 *   The import-method switching state machine on .toggle_import_option.
 */
export default class extends Controller {
  static targets = ['overlay', 'card']

  declare readonly hasOverlayTarget: boolean
  declare readonly overlayTarget: HTMLElement
  declare readonly hasCardTarget: boolean
  declare readonly cardTarget: HTMLElement

  private modernOverlay = false

  connect(): void {
    this.modernOverlay = (this.element as HTMLElement).dataset.modernOverlay === 'true'

    this.element.addEventListener('click', this.handleClick)
    this.element.addEventListener('change', this.handleChange)
    this.element.addEventListener('confirm:complete', this.handleConfirmComplete)
    if (this.modernOverlay) {
      this.element.addEventListener('ajax:complete', this.handleUploadAjaxComplete)
    }
  }

  disconnect(): void {
    this.element.removeEventListener('click', this.handleClick)
    this.element.removeEventListener('change', this.handleChange)
    this.element.removeEventListener('confirm:complete', this.handleConfirmComplete)
    this.element.removeEventListener('ajax:complete', this.handleUploadAjaxComplete)
    // DOM-replacement safety, mirroring processing_voters_component_controller.js's disconnect():
    // always clear the html-level lock, whatever caused this controller to disconnect.
    this.unlockPage()
  }

  // ---------------------------------------------------------------------------
  // Delegated handlers
  // ---------------------------------------------------------------------------

  private handleClick = (event: Event): void => {
    const target = event.target as HTMLElement

    // CSV upload button: show the modern overlay (flag-on) or NProgress (flag-off), then submit
    const uploadButton = target.closest<HTMLElement>('.upload_button')
    if (uploadButton) {
      event.preventDefault()
      if (this.modernOverlay) {
        this.showUploadOverlay()
      } else {
        NProgress.start()
      }
      const form = uploadButton.closest<HTMLFormElement>('form')
      if (form) Rails.fire(form, 'submit')
      return
    }

    // "Import from prior election" button: NProgress + in-progress label + submit
    const importButton = target.closest<HTMLElement>('.import_button')
    if (importButton) {
      event.preventDefault()
      NProgress.start()
      const span = importButton.querySelector('span')
      if (span) span.textContent = 'Importing...'
      const form = importButton.closest<HTMLFormElement>('form')
      if (form) Rails.fire(form, 'submit')
      return
    }

    // Import/validate: cosmetic NProgress.start, SKIPPED when the modern overlay flag is on (the
    // appended ProcessingVotersComponent supplies its own overlay + html-lock for that whole run —
    // running the old spinner too would just sit pointlessly behind the new backdrop).
    if (target.closest('.import_voter_list, .validate_voter_list')) {
      if (!this.modernOverlay) {
        NProgress.start()
      }
      return
    }

    // Start-over actions: cosmetic NProgress.start — separate flow, unchanged/out of scope here.
    if (target.closest('.start_over_mapper, .start_over_voter_list')) {
      NProgress.start()
      return
    }

    // Duplicate-voter wizard modal — open without allowing backdrop-click close
    const wizardLink = target.closest<HTMLElement>('#duplicate_wizard_path')
    if (wizardLink) {
      event.preventDefault()
      const $ = jquery()
      if ($) $(event.target as Element).modal({ clickClose: false })
      return
    }

    // Wizard submit: hide error banner + disable the submit button
    const wizardSubmit = target.closest<HTMLElement>('#submit_wizard_duplication')
    if (wizardSubmit) {
      document.getElementById('error_div')?.classList.add('hide')
      wizardSubmit.classList.add('disabled')
    }
  }

  private handleChange = (event: Event): void => {
    const target = event.target as HTMLElement

    // CSV drop zone: upload as soon as the file is dropped or picked — no Upload button any more
    // (Vinicius, 2026-08-11). Runs exactly what that button's click ran, so the overlay/NProgress
    // behaviour and the `ajax:complete` unlock below are unchanged. Covers BOTH gestures: the drop
    // zone re-dispatches a bubbling `change` on drop (drop_zone_component_controller.ts#drop), and
    // clicking through to the native picker fires one on its own.
    const uploadInput = target.closest<HTMLInputElement>('.upload-container input[type="file"]')
    if (uploadInput) {
      // Clearing the selection (the drop zone's own remove button) must not fire an empty upload.
      if (!uploadInput.files || uploadInput.files.length === 0) return

      if (this.modernOverlay) {
        this.showUploadOverlay()
      } else {
        NProgress.start()
      }
      const uploadForm = uploadInput.closest<HTMLFormElement>('form')
      if (uploadForm) Rails.fire(uploadForm, 'submit')
      return
    }

    // Column-mapper parse-option radios: submit enclosing form
    const parseOption = target.closest<HTMLElement>('.parse_data_option')
    if (parseOption) {
      event.preventDefault()
      const form = parseOption.closest<HTMLFormElement>('form')
      if (form) Rails.fire(form, 'submit')
    }
  }

  private handleConfirmComplete = (event: Event): void => {
    const target = event.target as HTMLElement
    if (target.closest('.start_over_mapper, .start_over_voter_list')) {
      NProgress.done()
    }
  }

  // Fires on the upload form itself (Rails UJS `ajax:complete`, both success and error branches —
  // voter_list_uploads/create.js.erb's own `NProgress.done()` at the end stays a safe no-op either
  // way). Scoped to `.upload-container` (the legacy _upload_form.html.erb form) so unrelated forms
  // in this panel (radio selection, column-mapper parse options, etc.) don't trigger it.
  private handleUploadAjaxComplete = (event: Event): void => {
    const target = event.target as HTMLElement
    if (target.classList?.contains('upload-container')) {
      this.hideUploadOverlay()
    }
  }

  private showUploadOverlay(): void {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove(OVERLAY_HIDDEN_CLASS)
    }
    if (this.hasCardTarget) {
      this.cardTarget.focus()
    }
    document.documentElement.classList.add(HTML_LOCK_CLASS)
  }

  private hideUploadOverlay(): void {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add(OVERLAY_HIDDEN_CLASS)
    }
    this.unlockPage()
  }

  private unlockPage(): void {
    document.documentElement.classList.remove(HTML_LOCK_CLASS)
  }
}
